import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { enqueueSnackbar } from "notistack"
import { Grid, MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { deletePaymentLink, fetchPaymentLinks, PaymentLinksQuery, PaymentLinksResponse } from "../paymentLinksApi"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { PaymentLink } from "../paymentLinksTypes"
import {
    MERCHANT_ADMIN_ROUTE,
    getEditPaymentLinkRout,
    MERCHANT_ADMIN_PAYMENT_LINK_CREATE_ROUTE,
} from "../../../../../constants/routes"

const defaultModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}
const selectPaymentLinks = (data: PaymentLinksResponse) => data?.paymentLinks || []

export function PaymentLinks() {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>(defaultModalState)

    const paymentLinksResult = useApiQuery(fetchPaymentLinks, {
        queryName: PaymentLinksQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectPaymentLinks,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(paymentLinksResult)

    const columns = useMemo<MRT_ColumnDef<PaymentLink>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("payment-links-table-name-column"),
                accessorKey: "title",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("payment-links-table-shop-column"),
                accessorKey: "shop.name",
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("payment-links-table-valid-from-column"),
                    accessorKey: "availabilityDetails.dateInterval.from",
                },
                (row) => row.availabilityDetails?.dateInterval?.from,
            ),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("payment-links-table-valid-to-column"),
                    accessorKey: "availabilityDetails.dateInterval.to",
                },
                (row) => row.availabilityDetails?.dateInterval?.to,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("status"),
                },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    const { mutate: callDeletePaymentLink } = useApiMutation(deletePaymentLink, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [PaymentLinksQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeletePaymentLink({ paymentLinksId: confirmModalState.itemId })
        } else {
            throw new Error("Payment links id is undefined")
        }
    }, [confirmModalState.itemId, callDeletePaymentLink])

    return (
        <>
            <LocalizedStrict id="payment-links-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Payment links"
                    page={Page.MERCHANT_PAYMENT_LINKS}
                    rightElement={
                        <LocalizedStrict id="payment-links-table-create-button">
                            <StandardButton onClick={() => navigate(MERCHANT_ADMIN_PAYMENT_LINK_CREATE_ROUTE)}>
                                Create new
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-shops">
                            <Breadcrumb>Shops</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-payment-links">
                            <Breadcrumb>Payment links</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="payment-links-table-menu-item-edit">
                            <MenuItem
                                onClick={() => {
                                    navigate(getEditPaymentLinkRout(row.original.id))
                                }}
                                key={`edit-${row.original.id}`}
                            >
                                Edit
                            </MenuItem>
                        </LocalizedStrict>,
                        <LocalizedStrict id="payment-links-table-menu-item-delete">
                            <MenuItem
                                onClick={() => {
                                    setConfirmModalState({
                                        showConfirmModal: true,
                                        confirmModalText: `${row.original.title}`,
                                        itemId: row.original.id,
                                    })
                                    closeMenu()
                                }}
                                key={`delete-${row.original.id}`}
                            >
                                Delete
                            </MenuItem>
                        </LocalizedStrict>,
                    ]}
                />
            </Grid>
            {confirmModalState.showConfirmModal && (
                <LocalizedStrict id="payment-links-confirm-delete-dialog" attrs={{ typeOfEntity: true }}>
                    <DeleteConfirmDialog
                        typeOfEntity="payment links"
                        entityName={confirmModalState.confirmModalText}
                        handleClick={handleDeleteClick}
                        open={confirmModalState.showConfirmModal}
                        onClose={() => setConfirmModalState(defaultModalState)}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
